import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {DeepPartial} from '@/global';
import type {Item} from '@PosterWhiteboard/items/item/item.class';
import {Animation, isAnimationApplicableForGIType, isAnimationApplicableOnItem} from '@PosterWhiteboard/animation/animation.class';
import {IntroAnimation} from '@PosterWhiteboard/animation/intro-animation.class';
import {OutroAnimation} from '@PosterWhiteboard/animation/outro-animation.class';
import {AnimationType} from '@PosterWhiteboard/items/item/item.types';

export enum AnimationSpeed {
  LOWER = 'lower',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  HIGHER = 'higher',
}

export enum SlideType {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum SpeedDuration {
  LOWER = 2.5,
  LOW = 1.7,
  MEDIUM = 1,
  HIGH = 0.5,
  HIGHER = 0.2,
}

export enum FadeSpeedDuration {
  LOWER = 2.7,
  LOW = 2.2,
  MEDIUM = 1.7,
  HIGH = 1,
  HIGHER = 0.3,
}

export enum SequentialAnimationSpeedDuration {
  LOWER = 1.2,
  LOW = 0.95,
  MEDIUM = 0.7,
  HIGH = 0.45,
  HIGHER = 0.2,
}

enum SlideshowTransitionDurations {
  LOWER_SPEED = 1.3,
  LOW_SPEED = 0.9,
  MED_SPEED = 0.5,
  HIGH_SPEED = 0.25,
  HIGHER_SPEED = 0.15,
}

export enum AnimateItemType {
  INTRO_ANIMATION = 'intro-animation',
  OUTRO_ANIMATION = 'outro-animation',
}

export enum PositionType {
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
  BOTTOM = 'bottom',
  TOP_LEFT = 'top-left',
  TOP_RIGHT = 'top-right',
  BOTTOM_LEFT = 'bottom-left',
  BOTTOM_RIGHT = 'bottom-right',
}

export interface AnimationObject {
  type: AnimationType;
  direction: SlideType | PositionType;
  speed: AnimationSpeed;
}

export const SLIDESHOW_INTRO_ANIMATIONS = [
  AnimationType.NONE,
  AnimationType.FADE,
  AnimationType.BLOCK,
  AnimationType.SHRINK,
  AnimationType.PEEK_DOWN,
  AnimationType.PEEK_UP,
  AnimationType.PEEK_LEFT,
  AnimationType.PEEK_RIGHT,
  AnimationType.WIPE_DOWN,
  AnimationType.WIPE_UP,
  AnimationType.WIPE_LEFT,
  AnimationType.WIPE_RIGHT,
  AnimationType.PAN_DOWN,
  AnimationType.PAN_UP,
  AnimationType.PAN_LEFT,
  AnimationType.PAN_RIGHT,
];

export class ItemAnimation extends CommonMethods {
  public type = AnimationType.NONE;
  public direction = PositionType.LEFT;
  public speed = AnimationSpeed.MEDIUM;

  public animation: Animation;
  public item: Item;

  constructor(item: Item, animationType: AnimateItemType) {
    super();
    this.item = item;
    if (animationType === AnimateItemType.INTRO_ANIMATION) {
      this.animation = new IntroAnimation(item);
    } else {
      this.animation = new OutroAnimation(item);
    }
  }

  public override copyVals(obj: DeepPartial<AnimationObject>): void {
    super.copyVals(obj);
    if (obj.type && isAnimationApplicableForGIType(this.item.gitype, obj.type)) {
      this.animation.copyVals(obj);
    } else {
      this.animation.copyVals({
        type: AnimationType.NONE,
        speed: AnimationSpeed.MEDIUM,
        direction: PositionType.LEFT,
      });
    }
  }

  public toObject(): AnimationObject {
    return {
      type: this.type,
      direction: this.direction,
      speed: this.speed,
    };
  }
}

export const hasSequentialDesignAnimationEffect = (type: AnimationType): boolean => {
  return (
    type === AnimationType.BLOCK ||
    type === AnimationType.POP ||
    type === AnimationType.TUMBLE ||
    type === AnimationType.JELLO ||
    type === AnimationType.ROTATE ||
    type === AnimationType.PAN ||
    type === AnimationType.PEEK ||
    type === AnimationType.WIPE
  );
};

export const getAnimationTime = (type: AnimationType, speed: AnimationSpeed, isTransition?: boolean): number => {
  if (isTransition) {
    switch (speed) {
      case AnimationSpeed.LOWER:
        return SlideshowTransitionDurations.LOWER_SPEED;

      case AnimationSpeed.LOW:
        return SlideshowTransitionDurations.LOW_SPEED;

      case AnimationSpeed.MEDIUM:
        return SlideshowTransitionDurations.MED_SPEED;

      case AnimationSpeed.HIGH:
        return SlideshowTransitionDurations.HIGH_SPEED;

      case AnimationSpeed.HIGHER:
        return SlideshowTransitionDurations.HIGHER_SPEED;

      default:
        console.error('Invalid transition speed value: ');
        return SlideshowTransitionDurations.MED_SPEED;
    }
  } else if (type === AnimationType.FADE) {
    switch (speed) {
      case AnimationSpeed.LOWER:
        return FadeSpeedDuration.LOWER;

      case AnimationSpeed.LOW:
        return FadeSpeedDuration.LOW;

      case AnimationSpeed.MEDIUM:
        return FadeSpeedDuration.MEDIUM;

      case AnimationSpeed.HIGH:
        return FadeSpeedDuration.HIGH;

      case AnimationSpeed.HIGHER:
        return FadeSpeedDuration.HIGHER;

      default:
        return 0;
    }
  } else if (hasSequentialDesignAnimationEffect(type)) {
    switch (speed) {
      case AnimationSpeed.LOWER:
        return SequentialAnimationSpeedDuration.LOWER;

      case AnimationSpeed.LOW:
        return SequentialAnimationSpeedDuration.LOW;

      case AnimationSpeed.MEDIUM:
        return SequentialAnimationSpeedDuration.MEDIUM;

      case AnimationSpeed.HIGH:
        return SequentialAnimationSpeedDuration.HIGH;

      case AnimationSpeed.HIGHER:
        return SequentialAnimationSpeedDuration.HIGHER;

      default:
        return 0;
    }
  } else {
    switch (speed) {
      case AnimationSpeed.LOWER:
        return SpeedDuration.LOWER;

      case AnimationSpeed.LOW:
        return SpeedDuration.LOW;

      case AnimationSpeed.MEDIUM:
        return SpeedDuration.MEDIUM;

      case AnimationSpeed.HIGH:
        return SpeedDuration.HIGH;

      case AnimationSpeed.HIGHER:
        return SpeedDuration.HIGHER;

      default:
        return 0;
    }
  }
};
